<template>
  <RevCard class="p-32">
    <h2 class="text-static-default-hi heading-3 mb-32 md:heading-2">
      {{ i18n(translations.title) }}
    </h2>
    <RevCard class="max-w-[40rem] p-24">
      <h3 class="text-static-default-hi heading-3 mb-16 md:heading-2">
        {{ i18n(translations.batteryFeeCardTitle) }}
      </h3>
      <p class="text-static-default-low body-1 mb-32">
        {{ i18n(translations.batteryFeeCardDescription) }}
      </p>
      <RevForm
        class="flex flex-col gap-24 md:flex-row md:items-start"
        :initial-values="{ batteryFee }"
        @submit="handleSubmit"
      >
        <RevInputText
          id="batteryFee"
          v-model="batteryFee"
          :label="i18n(translations.batteryFeeInputLabel)"
        />
        <RevButton
          class="w-full md:w-auto"
          :loading="isLoading"
          variant="primary"
          @click="handleSubmit"
        >
          {{ i18n(translations.batteryFeeFormSubmit) }}
        </RevButton>
      </RevForm>
    </RevCard>
  </RevCard>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'

import type { HttpError } from '@backmarket/http-api'
import {
  getRefurbisherFees,
  putRefurbisherFees,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevForm } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'

import { DEFAULT_BATTERY_FEE } from './PricingRules.constants'
import translations from './PricingRules.translations'

const i18n = useI18n()
const logger = useLogger()
const { openSuccessToast, openErrorToast } = useTheToast()

const isLoading = ref(false)
const batteryFee = ref(DEFAULT_BATTERY_FEE)

const { error: getRefurbisherFeesError, data: getRefurbisherFeesData } =
  await useHttpFetch(getRefurbisherFees)

if (getRefurbisherFeesData.value) {
  batteryFee.value = Number.parseFloat(
    getRefurbisherFeesData.value.batteryFee,
  ).toFixed(0)
}

async function handleSubmit() {
  isLoading.value = true
  try {
    if (!batteryFee.value) {
      batteryFee.value = DEFAULT_BATTERY_FEE
    }
    await $httpFetch(putRefurbisherFees, {
      body: {
        batteryFee: batteryFee.value,
      },
    })

    openSuccessToast()
    isLoading.value = false
  } catch (err) {
    const error = err as HttpError
    logger.error(
      '[BUYBACK][API][PRICING_RULES] Error updating refurbisher fees',
      {
        error,
        owners: ['bot-squad-circularity-offer-front'],
      },
    )

    openErrorToast()
    isLoading.value = false
  }
}

onMounted(() => {
  if (getRefurbisherFeesError.value) {
    const error = getRefurbisherFeesError.value
    logger.error('[BUYBACK][API][PRICING_RULES] Error get refurbisher fees', {
      error,
      owners: ['bot-squad-circularity-offer-front'],
    })
    openErrorToast()
  }
})
</script>
